import type { IconName } from "@bundle/tokens";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { Icon } from "./Icon.tsx";
import { Spinner } from "./Spinner.tsx";
import { color, cx, type ColorToken } from "./tokens.ts";

/** A database option colour as a chip tone: `tag.red` → `tag.red.bg` / `tag.red.fg`. */
export type ChipTagTone = ColorToken extends infer T ? (T extends `tag.${infer C}.bg` ? `tag.${C}` : never) : never;

export type ChipTone = "neutral" | "accent" | "agent" | "external" | "mention" | "info" | "success" | "warning" | "danger" | ChipTagTone;

export type ChipProps = {
  /** Already translated, or user content (an option name). */
  label: string;
  /** Colour pair (default neutral = chip.bg / chip.fg). Status tones use status.<kind>.bg / .fg; `tag.<color>` the option colours. */
  tone?: ChipTone;
  /** Leading glyph in the tone's foreground. */
  icon?: IconName;
  /** A filter chip's on/off state (`aria-pressed`; accent outline when on). Needs `onPress`. */
  selected?: boolean;
  /** Present = the chip is a button. */
  onPress?: () => void;
  /** The button's name when the label alone doesn't say what pressing does ("Bundle is in beta. Open Help"). */
  accessibilityLabel?: string;
  /** Working on it: a spinner takes the icon's place (announced with `loadingLabel`). */
  loading?: boolean;
  loadingLabel?: string;
  /** Present = a trailing × button that removes the chip. */
  onRemove?: () => void;
  /** A second, trailing button with its own job (an attachment's "Download" beside the chip's "Open"). Not with `onRemove`. */
  trailingAction?: { icon: IconName; label: string; onPress: () => void; testID?: string };
  /** Name of the × button (default "Remove <label>"). */
  removeLabel?: string;
  /** Required when pressable or removable; the × is `<testID>-remove`. */
  testID?: string;
};

const named = new Set(["neutral", "accent", "agent", "external", "mention", "info", "success", "warning", "danger"]);

/** A chip tone's colours for another chip-like mark (a tab's count): a `b-tag-<tone>` class or `tag.<color>` style. */
export function chipTone(tone: ChipTone): { className: string | undefined; style: { background: string; color: string } | undefined } {
  if (named.has(tone)) return { className: `b-tag-${tone}`, style: undefined };
  return { className: undefined, style: { background: color(`${tone}.bg` as ColorToken), color: color(`${tone}.fg` as ColorToken) } };
}

/**
 * A small label in a colour: database options and statuses, "Agent" / "External" markers, filter chips, removable
 * picks. One look everywhere: radius full, caption medium, 20 high.
 */
export function Chip({ label, tone = "neutral", icon, selected, onPress, accessibilityLabel, loading, loadingLabel, onRemove, removeLabel, trailingAction, testID }: ChipProps) {
  const t = usePrimitiveT();
  const { className: toneClass, style } = chipTone(tone);
  const body = (
    <>
      {loading === true ? <Spinner size="icon.sm" color="text.tertiary" {...(loadingLabel === undefined ? {} : { accessibilityLabel: loadingLabel })} /> : icon === undefined ? null : <Icon name={icon} size="icon.sm" />}
      <span className="b-tag-label">{label}</span>
    </>
  );
  return (
    <span
      className={cx("b-tag", toneClass, onPress !== undefined && "b-tag-pressable", selected === true && "b-tag-selected", (onRemove !== undefined || trailingAction !== undefined) && "b-tag-removable")}
      style={style}
      data-testid={onPress === undefined ? testID : undefined}
    >
      {onPress === undefined ? (
        body
      ) : (
        <button type="button" className="b-tag-main" aria-pressed={selected} aria-busy={loading} aria-label={accessibilityLabel} onClick={(e) => {
          e.stopPropagation();
          onPress();
        }} data-testid={testID}>
          {body}
        </button>
      )}
      {trailingAction === undefined ? null : (
        <button
          type="button"
          className="b-tag-remove"
          aria-label={trailingAction.label}
          title={trailingAction.label}
          onClick={(e) => {
            e.stopPropagation();
            trailingAction.onPress();
          }}
          data-testid={trailingAction.testID}
        >
          <Icon name={trailingAction.icon} size="icon.sm" />
        </button>
      )}
      {onRemove === undefined ? null : (
        <button
          type="button"
          className="b-tag-remove"
          aria-label={removeLabel ?? t("primitive.chip.remove", { label })}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          data-testid={testID === undefined ? undefined : `${testID}-remove`}
        >
          <Icon name="close" size="icon.sm" />
        </button>
      )}
    </span>
  );
}

export type ChipButtonProps = Pick<ChipProps, "label" | "icon" | "selected" | "testID"> & Omit<ComponentPropsWithoutRef<"button">, "children">;

/** A filter chip that opens a menu or popover: its trigger (a `Menu` child), with the chevron that says so. */
export const ChipButton = forwardRef<HTMLButtonElement, ChipButtonProps>(function ChipButton({ label, icon, selected, testID, ...button }, ref) {
  return (
    <span className={cx("b-tag", "b-tag-neutral", "b-tag-pressable", selected === true && "b-tag-selected")}>
      <button {...button} ref={ref} type="button" className="b-tag-main" data-testid={testID}>
        {icon === undefined ? null : <Icon name={icon} size="icon.sm" />}
        <span className="b-tag-label">{label}</span>
        <Icon name="chevronDown" size="icon.sm" />
      </button>
    </span>
  );
});
